import { __ } from '@wordpress/i18n';
import { RawHTML, useEffect, useState } from "@wordpress/element";
import { useBlockProps, InnerBlocks, InspectorControls, ColorPalette } from '@wordpress/block-editor';
import { __experimentalHeading as Heading, PanelBody, ToggleControl, __experimentalUnitControl as UnitControl, Modal, TextControl} from '@wordpress/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import * as solidIcons from '@fortawesome/free-solid-svg-icons';
import * as regularIcons from '@fortawesome/free-regular-svg-icons';
import * as brandIcons from '@fortawesome/free-brands-svg-icons';
import useDebounce from './hooks/useDebounce';

export const edit = ({ clientId, attributes, setAttributes }) => {

    const {
        id,
        icon,
        faIconSize,
        faIconColor,
        isLinkable,
        url,
        openInNewTab
    } = attributes;

    const [iconList, setIconList] = useState([]);
    const [filteredIconList, setFilteredIconList] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        if (!id && clientId) {
            const clientIdSliced = clientId.slice(clientId.lastIndexOf('-') + 1);
            setAttributes({ id: `hfc-fpico-${clientIdSliced}` });
        }
    }, [id, clientId, setAttributes]);
    

    // Defined Block Props.
    const blockProps = useBlockProps();

    // Function to filter valid FontAwesome icons
    const filterValidIcons = (iconSet) => {
        return Object.values(iconSet).filter(
            (icon) => icon && icon.prefix && icon.iconName
        );
    };

    // Add all icons to the library (solid, regular, brands)
    library.add(
        ...filterValidIcons(solidIcons),
        ...filterValidIcons(regularIcons),
        ...filterValidIcons(brandIcons)
    );

    useEffect(() => {
        const iconsArray = Object.entries({
            ...solidIcons,
            ...regularIcons,
            ...brandIcons
        })
        .filter(([key, icon]) => 
            icon && 
            icon.prefix && 
            icon.iconName && 
            key.startsWith('fa')
        )
        .map(([iconName, icon]) => ({
            iconName,
            icon     
        }));
    
        setIconList(iconsArray);
        setFilteredIconList(iconsArray);
    }, [isModalOpen]);  

    // Function to get icon by name
    const getFontAwesomeIcon = (iconName) => {
        if (!iconName) {
            return solidIcons['faCoffee'];
        }
        return solidIcons[iconName] || regularIcons[iconName] || brandIcons[iconName] || solidIcons['faCoffee'];
    };
     // Set default icons
     useEffect(() => {
        if (!icon) {
            setAttributes({ icon: 'faCoffee' }); // Default fallback icon
        }
    }, [icon, setAttributes]);
    
    // Search icon implementation start
    const debouncedSearchTerm = useDebounce(searchTerm, 800); 

    useEffect(() => {
        if (debouncedSearchTerm) {
            // Filter the icons based on the debounced search term
            const filteredIcons = iconList.filter(icon =>
                icon.iconName.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
            );
            setFilteredIconList(filteredIcons);
        } else {
            // If no search term, show all icons
            setFilteredIconList(iconList);
        }
    }, [debouncedSearchTerm, iconList]);

    const handleSearchInput = (e) => {
        setSearchTerm(e.target.value); 
    };
    // Search icon implementation end
    
    // Icon Picker function
    const openIconPicker = () => {
        setSearchTerm(''); 
        setFilteredIconList(iconList);
        setIsModalOpen(true);
    };    

    // Change Icon function
    const handleIconChange = (iconItem) => {
        if (iconItem && iconItem.iconName) {
            setAttributes({ icon: iconItem.iconName });
        } else {
            console.error('Invalid icon item:', iconItem);
        }
        setSearchTerm('');
        setFilteredIconList(iconList);
        setIsModalOpen(false);
    };          

    // Modal close
    const closeModal = () => setIsModalOpen(false);

    return ([
        <InspectorControls key={`inspector-controls-${id}`}>
            <PanelBody
                title={__('Icon Pack Block Settings')}
                icon={'admin-generic'}
                initialOpen={true}
                className={'frhd__control frhd__control-query'}
            >
                <Heading className={'frhd__pb-heading'}>Icon Color :</Heading>
                <ColorPalette
                    value={faIconColor}
                    onChange={(colorValue) => setAttributes({ faIconColor: colorValue })}
                    clearable={false}
                />
                <UnitControl
                    label={__('Icon Size:')}
                    onChange={newValue => setAttributes({ faIconSize: newValue })}
                    isUnitSelectTabbable
                    value={faIconSize}
                    className={'frhd__component frhd__component-width-90'}
                />
                    <ToggleControl
                    label={__('Link Icon')}
                    checked={isLinkable}
                    onChange={(newValue) => setAttributes({ isLinkable: newValue })}
                    __nextHasNoMarginBottom
                />
                {isLinkable && (
                    <>
                        <TextControl
                            label={__('Link URL')}
                            value={url}
                            onChange={(newUrl) => setAttributes({ url: newUrl })}
                            placeholder={'#'}
                            __nextHasNoMarginBottom
                        />
                        <ToggleControl
                            label={__('Open in New Tab')}
                            checked={openInNewTab}
                            onChange={(newValue) => setAttributes({ openInNewTab: newValue })}
                            __nextHasNoMarginBottom
                        />
                    </>
                )}
            </PanelBody>
        </InspectorControls>,
        <div {...blockProps}>
            <div id={id} className='fp-ip-container'>
                {isLinkable ? (
                    <a 
                        href={url} 
                        target={openInNewTab ? '_blank' : '_self'}
                        rel="noopener"
                        onClick={(e) => {
                            const isEditor = window.wp.data.select('core/editor').getCurrentPostType();
                            if (isEditor) {
                                e.preventDefault();
                            }
                        }}
                    >
                        <FontAwesomeIcon 
                            icon={getFontAwesomeIcon(icon)} 
                            fontSize={faIconSize}
                            color={faIconColor}
                            onClick={(e) => {
                                e.preventDefault();
                                setIsModalOpen(true);
                            }}
                            style={{ width: faIconSize, height: faIconSize }}
                        />
                    </a>
                    ) : (
                        <FontAwesomeIcon 
                            icon={getFontAwesomeIcon(icon)}
                            fontSize={faIconSize}
                            color={faIconColor}
                            onClick={() => setIsModalOpen(true)}
                            style={{ width: faIconSize, height: faIconSize }}
                        />
                    )}
            </div>
        </div>,
            isModalOpen && (
                <Modal title="Replace Icon" onRequestClose={closeModal}>
                    <div className='fp-ip-modal-inner-wrapper'>
                        <div className='fp-ip-modal-form-wrapper'>
                            <form className='fp-icons-search-form'>
                                <input
                                    className="fp-tabs-icon-search"
                                    type="text"
                                    placeholder="Search icon"
                                    value={searchTerm}
                                    onChange={handleSearchInput}
                                />
                            </form>
                        </div>
                        <div className='fp-ip-modal-icons-wrapper'>
                            {filteredIconList.map((iconItem, index) => (
                            <div className='fp-ip-modal-icons' key={index}>
                                <a 
                                    href='#' 
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleIconChange(iconItem);
                                    }} 
                                    className='fp-ip-modal-icon'
                                >
                                    <FontAwesomeIcon icon={iconItem.icon} />
                                </a>
                            </div>
                        ))}
                        </div>
                    </div>
                </Modal>
            )
    ])
}
export const save = ({ attributes }) => {
    const {
        icon,
        faIconSize,
        faIconColor,
        isLinkable,
        url,
        openInNewTab
    } = attributes;
    
    const getFontAwesomeIcon = (iconName) => {
        return solidIcons[iconName] || regularIcons[iconName] || brandIcons[iconName] || solidIcons['faCoffee']; 
    };
    const blockProps = useBlockProps.save();
    return (
        <div {...blockProps}>
            {isLinkable ? (
                <a 
                    href={url} 
                    target={openInNewTab ? '_blank' : '_self'}
                    rel="noopener noreferrer"
                >
                    <FontAwesomeIcon 
                        icon={getFontAwesomeIcon(icon)} 
                        width={faIconSize}
                        height={faIconSize}
                        color={faIconColor}
                    />
                </a>
            ) : (
                <FontAwesomeIcon 
                    icon={getFontAwesomeIcon(icon)} 
                    width={faIconSize}
                    height={faIconSize}
                    color={faIconColor}
                />
            )}
        </div>
    );
};